import { Injectable } from '@angular/core';
import { ConfigConditionService } from '../config-condition.service';
import { ICustomMessage, ITableColumn, ITableConfig, ITableData } from '../config-report.interface';

export const rowSpanKey: string = 'rowSpan';

export enum TEXT_ALIGNMENT {
  RIGHT = 'right',
  LEFT = 'left',
}

export interface IRowMetaData {
  rowSpan1?: boolean;
  rowSpan2?: boolean;
  rowSpan3?: boolean;
  colSpan?: number;
  isLastRow?: boolean;
  noDataExists?: boolean;
  isFirstRow?: boolean;
  groupedRowSummaryExists?: boolean;
}

@Injectable()
export class TableUtilsService {
  constructor(private configConditionService: ConfigConditionService) {}

  /**
   * gives column configs of columns that needs to be grouped.
   * also sort these columns depening upon thier hierarchy postion
   * @param tableConfig the full table config used by report
   * @returns column configs of only the columns that has grouping in them
   */
  public getGroupedRowsConfig(tableConfig: ITableConfig): ITableColumn[] {
    let groupedRowsConfig: ITableColumn[] =
      tableConfig.column.filter((configItem: ITableColumn) => configItem.grouped_row_key)
    ;

    // sorting based on hierarchy position as first hierarchy need to be
    // transformed before second hierarchy
    groupedRowsConfig = groupedRowsConfig
      .sort((groupedRowA, groupedRowB) =>
        parseInt(groupedRowA.grouped_hierarchy_position, 10) -
        parseInt(groupedRowB.grouped_hierarchy_position, 10),
      )
    ;

    return groupedRowsConfig.length ? groupedRowsConfig : null;
  }

  /**
   * convert single object with specific array into muliple objects depending upon elements in the array
   * also flattens the object after reciving it
   * @param data data which need to be expanded
   * @param columnConfig config which tells us on which array data needs to be expanded
   * @param mergeFirstGroup boolean telling if new data should be merged with old data
   * @returns an exapanded and flattened (non-nested) object
   */
  public flattenArraysInData(
    data: ITableData[],
    groupedRowColumnConfig: ITableColumn[],
    mergeFirstGroup: boolean = false,
  ): ITableData[] {
    let reducedData: ITableData[] = data;
    groupedRowColumnConfig.forEach((groupedRowConfigItem: ITableColumn)=> {
      reducedData = reducedData.reduce<ITableData[]>(
        (
          result: ITableData[],
          { [groupedRowConfigItem.grouped_row_key]: arrayItems, ...item }: ITableData,
          dataIndex: number,
        ): ITableData[] => {
          if (arrayItems?.length) {
            return result.concat(
              arrayItems.map((arrayItem: ITableData[], itemIndex: number) =>
                ({
                  ...item,
                  ...arrayItem,
                  ...this.getRowMetadata(
                    groupedRowConfigItem,
                    arrayItems.length,
                    dataIndex === 0 && mergeFirstGroup,
                    itemIndex,
                  ),
                }),
              ),
            );
          } else {
            return result.concat({ ...item, ...this.getRowMetadata(groupedRowConfigItem, 0) });
          }
        },
        [],
      );
    });
    const flattenedData: ITableData[] = reducedData.map((dataItem: ITableData) => this.flattenObject(dataItem));
    return flattenedData;
  }

  /**
   * flatens nested object into non-nested object
   * example
   * turns {vehicle: {number; "4321"}, trip: {id: 1234}}
   * to {vehcile.number: "4321", trip.id: 1234}
   * @param data object that needs to be flattened
   * @param parentKey used in recursion, tells what was parent key, initially is empty string
   * @returns flattened non nested object
   */
  public flattenObject(data: ITableData, parentKey: string = ''): ITableData {
    if (parentKey !== '') parentKey += '.';
    const flattened = {};
    Object.keys(data).forEach((key: string) => {
      if (typeof data[key] === 'object' && data[key] !== null && !Array.isArray(data[key])) {
        Object.assign(flattened, this.flattenObject(data[key], parentKey + key));
      } else {
        flattened[parentKey + key] = data[key];
      }
    });
    return flattened;
  }

  /**
   * returns metadata for data depending on config and data which is later helps in generating UI
   * @param groupedRowConfigItem config item from which we get information about column
   * @param arrayLength is used find out rowspan for our data element
   * @param haveToMergeData wether data coming is to be merged with last group
   * @param arrayItemindex used to find out last and first row in data
   * @returns returns object with row metaData like rowSpan, noDataExists, isLastRow etc.
   */
  public getRowMetadata(
    groupedRowConfigItem: ITableColumn,
    arrayLength: number,
    haveToMergeData: boolean = false,
    arrayItemindex: number = 0,
  ): IRowMetaData {
    const rowMetaData: IRowMetaData = {};

    rowMetaData[rowSpanKey + groupedRowConfigItem.grouped_hierarchy_position] = true;
    // works only for first heirarical position, column spanned row doesnt exists in nested grouped row column
    rowMetaData['colSpan'] = groupedRowConfigItem.no_data.grouped_row_columns.length ?? 0;

    if (arrayItemindex === 0 && !haveToMergeData) {
      rowMetaData['isFirstRow'] = true;
      rowMetaData['noDataExists'] = arrayLength === 0;
    } else {
      rowMetaData['isFirstRow'] = false;
    }

    if (arrayItemindex === arrayLength - 1 || arrayLength === 0) {
      rowMetaData['isLastRow'] = true;
      if (groupedRowConfigItem?.grouped_row_summary?.length) rowMetaData['groupedRowSummaryExists'] = true;
    }

    return rowMetaData;
  }

  /**
   * Adds MetaData for Custom Message which is used later on to generate custom message
   * @remarks This function changes the input tableData that is passed
   * @param tableData the data in which custom message metadata is to be added (usually flattened data)
   * @param tableConfig whole table config
   */
  public addCustomMessageMetadata(tableData: ITableData[], tableConfig: ITableConfig): void {
    const customMessageConfig: ICustomMessage = tableConfig.custom_message;

    if (customMessageConfig) {
      tableData.forEach((dataItem: ITableData, index: number) => {
        if (this.configConditionService.evaluateCondition(
          customMessageConfig.show_condition_config.name, null, tableData, index)
        ) {
          dataItem['shouldShowCustomMessage'] = true;
        }
      });
    }
  }

  /**
   * Merged summary data of pervious page with new data belonging to the same group.
   * We need to update this because the summary data cannot be calculated again on new page by BE.
   * For more information check ticket AM-9666
   * @remarks This function changes the input tableData that is passed
   * @param tableConfig whole table data
   * @param groupedRowConfigItem config item from which we get information about grouped columns
   * @param lastRowData last row data which is used to find out which group was merged
   * @param lastRowIndex index of last row which is used to find out where to start the search
   * @returns column config for grouped column
   */
  public updateGroupedRowSummaryData(
    tableData: ITableData[],
    groupedRowColumnConfig: ITableColumn[],
    lastRowData: ITableData,
    lastRowIndex: number,
  ): ITableData[] {
    const lastRowOfFirstGroup = this.getLastRowOfGroup(
      tableData,
      groupedRowColumnConfig[0].key,
      lastRowData[groupedRowColumnConfig[0].key],
      lastRowIndex,
    );

    // Taking out keys of summary items, and adding old summary data to new last row of group after merging them
    groupedRowColumnConfig[0].grouped_row_summary
      .map((summaryItem: ITableColumn) => summaryItem.key)
      .forEach((key: string) => lastRowOfFirstGroup[key] = lastRowData[key])
    ;

    return tableData;
  }

  private getLastRowOfGroup(
    tableData: ITableData[],
    groupedRowKey: string,
    groupedEntityData: string,
    lastRowIndex: number,
  ): ITableData {
    return tableData
      .slice(lastRowIndex, tableData.length)
      .filter((dataItem: ITableData) => dataItem[groupedRowKey] === groupedEntityData && dataItem.isLastRow)
      [0]
    ;
  }
}
